import React from 'react';
import PropTypes from 'prop-types';
import { useFormContext } from 'react-hook-form';

import { intlMessage } from 'constants/propTypes';
import { bem } from 'helpers/class-name';
import { CardSection } from 'components/Card';
import { openDecagonChat } from 'components/Decagon';
import { CharactersRemaining } from 'components/Form';
import { ButtonGroup, Dropdown, Textarea } from 'components/HookForm';
import { DropdownItem, DropdownMenu, DropdownToggle } from 'components/Dropdown2';
import Button from 'components/Button';
import Message from 'components/Message';
import When from 'components/When';
import { LONG_TEXT_MAX_CHARACTERS } from 'constants/logic';
import messages from '../ReservationFeedbackModalV2.messages';
import ReportBadge from './ReportBadge';

import './ReportFormPanel.scss';

const { block, element } = bem('report-form-panel');

const ADDITIONAL_DETAILS_FORM_FIELD_NAME = 'additionalDetails';
const DISRUPTIVE_REASON_FORM_FIELD_NAME = 'disruptiveReason';

const DISRUPTIVE_REASONS = [
  messages.disruptiveReasonNotFollowingPolicy,
  messages.disruptiveReasonRepeatedlyLate,
  messages.disruptiveReasonRudeToStaff,
];

const ReportFormPanel = ({ reasonType, isMobile, handleClose, handleSubmit, onSubmitReport, formId }) => {
  const { watch } = useFormContext();
  const additionalDetails = watch(ADDITIONAL_DETAILS_FORM_FIELD_NAME) || '';
  const isDisruptiveUser = reasonType === messages.disruptiveUser;

  return (
    <div className={block()}>
      <When predicate={!isMobile}>
        <ReportBadge label={messages.guidancePanelReportingFor} text={reasonType} />
      </When>

      <When predicate={isDisruptiveUser}>
        <div className={element('reason-dropdown-section')}>
          <Dropdown
            className={element('reason-dropdown')}
            name={DISRUPTIVE_REASON_FORM_FIELD_NAME}
            required
            validate={{
              message: messages.selectReasonLabel,
              value: fieldValue => !!fieldValue,
            }}>
            {({ value: selectedOption, onChange }) => (
              <>
                <DropdownToggle className={element('reason-dropdown-toggle', { selected: selectedOption })}>
                  <When predicate={!selectedOption}>
                    <Message as="div" className="text--light" message={messages.selectReasonLabel} />
                  </When>
                  <When predicate={!!selectedOption}>
                    <Message as="div" message={selectedOption} />
                  </When>
                </DropdownToggle>
                <DropdownMenu className={element('reason-dropdown-menu')}>
                  {DISRUPTIVE_REASONS.map(reason => (
                    <DropdownItem
                      key={reason.id}
                      isSelected={selectedOption?.id === reason.id}
                      onClick={() => onChange(reason)}>
                      <Message message={reason} />
                    </DropdownItem>
                  ))}
                </DropdownMenu>
              </>
            )}
          </Dropdown>
          <Message
            className={element('serious-behavior-guidance')}
            message={messages.seriousBehaviorGuidance}
            values={{
              here: text => (
                <Button styled="inline-link" onClick={openDecagonChat}>
                  {text}
                </Button>
              ),
            }}
          />
        </div>
      </When>

      <div className={element('additional-details-section')}>
        <Message as="div" className={element('additional-details-label')} message={messages.additionalDetailsLabel} />
        <Textarea
          name={ADDITIONAL_DETAILS_FORM_FIELD_NAME}
          className={element('additional-details-textarea')}
          maxLength={LONG_TEXT_MAX_CHARACTERS}
          required
          placeholder={messages.additionalDetailsPlaceholder}
        />
        <When predicate={LONG_TEXT_MAX_CHARACTERS - additionalDetails.length <= 10}>
          <div
            className={element('characters-remaining', {
              error: LONG_TEXT_MAX_CHARACTERS === additionalDetails.length,
            })}>
            <CharactersRemaining length={LONG_TEXT_MAX_CHARACTERS} value={additionalDetails} showMaxLength />
          </div>
        </When>
        <div className={element('feedback-disclosure')}>
          <Message message={messages.feedbackDisclosure} />
        </div>
      </div>

      <CardSection flush className={element('footer')}>
        <ButtonGroup className={element('button-group')} divider={false} flush>
          <Message
            as={Button}
            className={element('cancel-btn')}
            message={messages.cancelButton}
            onClick={handleClose}
            styled="inline-link"
            type={Button.TYPES.BUTTON}
          />
          <Message
            as={Button}
            form={formId}
            className={element('submit-btn')}
            message={messages.submitButton}
            styled="primary"
            type={Button.TYPES.SUBMIT}
            onClick={handleSubmit(onSubmitReport)}
          />
        </ButtonGroup>
      </CardSection>
    </div>
  );
};

ReportFormPanel.propTypes = {
  reasonType: intlMessage.isRequired,
  isMobile: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  onSubmitReport: PropTypes.func.isRequired,
  formId: PropTypes.string.isRequired,
};

export default ReportFormPanel;
